/* CountryComplianceBadges — small pills next to the workspace country pill
 * showing country-specific compliance items that are *active* in feature_flags
 * AND match the current project's domain.
 *
 * Pills appear ONLY when (1) the corresponding flag is on AND (2) the pill's
 * `domain` matches the project's domain (or the pill has no `domain` filter).
 * Hovering shows the regulatory body or scheme name.
 */
import React from "react";
import { Award, ShieldCheck, FileCheck2, Leaf, Building2, Hammer } from "lucide-react";
import { useFeatureFlags } from "../lib/featureFlags";

// flag dotted-path → pill descriptor.
// `domain` (optional) restricts the pill to a specific project domain so
// e.g. RERA does not show on infrastructure projects in India.
const COMPLIANCE_PILLS = [
  // SG
  { flag: "real_estate.deliverables.green_mark_minimum_certified",
    domain: "real_estate",
    label: "Green Mark", title: "BCA Green Mark — mandatory for new buildings (SG)",
    icon: Leaf, color: "#22c55e" },
  { flag: "real_estate.deliverables.scdf_fire_safety_plan",
    domain: "real_estate",
    label: "SCDF Fire", title: "SCDF Fire Safety Plan (Singapore Civil Defence Force)",
    icon: ShieldCheck, color: "#ef4444" },
  { flag: "real_estate.deliverables.bca_buildability_score",
    domain: "real_estate",
    label: "Buildability", title: "BCA Buildability Score (SG)",
    icon: Hammer, color: "#0ea5e9" },
  // UK
  { flag: "real_estate.deliverables.cdm_principal_designer_pack",
    domain: "real_estate",
    label: "CDM Pack", title: "CDM 2015 Principal Designer Pack (UK)",
    icon: ShieldCheck, color: "#3b82f6" },
  { flag: "real_estate.deliverables.riba_stage_reporting",
    domain: "real_estate",
    label: "RIBA", title: "RIBA Plan of Work 2020 stage reporting (UK)",
    icon: FileCheck2, color: "#8b5cf6" },
  { flag: "infrastructure.deliverables.dmrb_compliance_pack",
    domain: "infrastructure",
    label: "DMRB", title: "Design Manual for Roads and Bridges (UK Highways)",
    icon: FileCheck2, color: "#8b5cf6" },
  // AU
  { flag: "real_estate.deliverables.nabers_rating",
    domain: "real_estate",
    label: "NABERS", title: "NABERS energy/water rating (AU)",
    icon: Award, color: "#f59e0b" },
  { flag: "real_estate.deliverables.ncc_section_j_pack",
    domain: "real_estate",
    label: "NCC Sec J", title: "NCC Section J energy efficiency (AU)",
    icon: Leaf, color: "#22c55e" },
  { flag: "process_plant.deliverables.whs_compliance_pack",
    domain: "process_plant",
    label: "WHS", title: "WHS Act 2011 compliance (AU)",
    icon: ShieldCheck, color: "#ef4444" },
  // ME
  { flag: "process_plant.deliverables.saes_compliance",
    domain: "process_plant",
    label: "SAES", title: "Saudi Aramco SAES compliance",
    icon: FileCheck2, color: "#10b981" },
  { flag: "process_plant.deliverables.adnoc_hse",
    domain: "process_plant",
    label: "ADNOC HSE", title: "ADNOC HSE standards (UAE)",
    icon: ShieldCheck, color: "#10b981" },
  // US
  { flag: "process_plant.deliverables.osha_psm_compliance",
    domain: "process_plant",
    label: "OSHA PSM", title: "OSHA Process Safety Management (US)",
    icon: ShieldCheck, color: "#3b82f6" },
  // IN
  { flag: "real_estate.deliverables.rera_compliance",
    domain: "real_estate",
    label: "RERA", title: "Real Estate (Regulation and Development) Act compliance (IN)",
    icon: Building2, color: "#f97316" },
  // SG infra
  { flag: "infrastructure.deliverables.lta_civil_design_pack",
    domain: "infrastructure",
    label: "LTA", title: "LTA Civil Design Criteria (SG Land Transport)",
    icon: FileCheck2, color: "#0ea5e9" },
  { flag: "infrastructure.deliverables.pub_drainage_compliance",
    domain: "infrastructure",
    label: "PUB", title: "PUB Code of Practice — Surface Water Drainage (SG)",
    icon: FileCheck2, color: "#06b6d4" },
];

export default function CountryComplianceBadges({ projectDomain, size = 11 }) {
  const ff = useFeatureFlags();
  if (!ff.ready) return null;
  const visible = COMPLIANCE_PILLS.filter((p) => {
    if (!ff.isOn(p.flag, false)) return false;
    // If pill has a domain restriction, project's domain must match
    if (p.domain && projectDomain && p.domain !== projectDomain) return false;
    return true;
  });
  if (!visible.length) return null;

  return (
    <div data-testid="workspace-compliance-badges"
          style={{ display: "inline-flex", flexWrap: "wrap", gap: 4,
                    alignItems: "center" }}>
      {visible.map((p) => {
        const Icon = p.icon;
        return (
          <div
            key={p.flag}
            data-testid={`compliance-pill-${p.flag.split(".").pop()}`}
            title={p.title}
            style={{
              display: "inline-flex", alignItems: "center", gap: 4,
              padding: "3px 7px", borderRadius: 4,
              border: `1px solid ${p.color}66`,
              background: `${p.color}14`,
              color: p.color, fontSize: size, fontWeight: 600,
              fontFamily: "JetBrains Mono", letterSpacing: 0.3,
              cursor: "help",
            }}
          >
            <Icon size={size + 1} />
            <span>{p.label}</span>
          </div>
        );
      })}
    </div>
  );
}
